<template>
  <div class="terminal-container h-full min-h-[400px]">
    <div v-if="status === 'connecting'" class="terminal-loading">
      <span class="loading loading-spinner"></span>
      <span>Waiting for agent to connect...</span>
    </div>
    <div v-else-if="status === 'connected' || term" class="terminal-connected">
      <div ref="terminalRef" class="terminal-body"></div>
      <button
        v-if="status === 'connected'"
        class="terminal-close"
        title="Close session"
        @click="closeSession"
      >
        ✕
      </button>
      <button
        v-else-if="term"
        class="terminal-close"
        title="Close terminal"
        @click="emit('close')"
      >
        Đóng
      </button>
    </div>
    <div v-else class="terminal-closed">
      <span>Session ended</span>
      <button class="btn btn-sm btn-primary" @click="emit('close')">
        Đóng
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
defineOptions({ name: "ClientTerminal" });

import { nextTick, onBeforeUnmount, onMounted, ref } from "vue";
import { Terminal } from "@xterm/xterm";
import { FitAddon } from "@xterm/addon-fit";
import { WebLinksAddon } from "@xterm/addon-web-links";
import { createDisconnectHandler } from "./terminalSession";
import "@xterm/xterm/css/xterm.css";

const props = defineProps<{
  actionId: string;
}>();

const emit = defineEmits<{
  close: [];
}>();

const terminalRef = ref<HTMLElement>();
const status = ref<"connecting" | "connected" | "closed">("connecting");

let term: Terminal | null = null;
let fitAddon: FitAddon | null = null;
let ws: WebSocket | null = null;
let resizeObserver: ResizeObserver | null = null;
let unmounted = false;
const endSession = createDisconnectHandler(
  (text) => {
    if (!unmounted) term?.write(text);
  },
  () => (status.value = "closed"),
);

function getWsUrl(): string {
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  const host = window.location.host;
  return `${proto}//${host}/api/v1/view/terminal/ws/${props.actionId}`;
}

function initTerminal() {
  if (!terminalRef.value) return;
  term = new Terminal({
    cursorBlink: true,
    fontSize: 14,
    theme: {
      background: "#1e1e1e",
      foreground: "#d4d4d4",
    },
  });
  fitAddon = new FitAddon();
  term.loadAddon(fitAddon);
  term.loadAddon(new WebLinksAddon());
  term.open(terminalRef.value);
  fitAddon.fit();
  // xterm captures keyboard input via a hidden textarea; if it isn't focused the
  // user's keystrokes never reach onData and the terminal looks read-only. Focus
  // on init and keep focus on click (the pane can steal it on scroll/route).
  term.focus();
  terminalRef.value.addEventListener("click", () => term?.focus());

  term.onData((data) => {
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(new TextEncoder().encode(data));
    }
  });

  resizeObserver = new ResizeObserver(() => {
    fitAddon?.fit();
    if (ws?.readyState === WebSocket.OPEN && term) {
      ws.send(
        JSON.stringify({ type: "resize", cols: term.cols, rows: term.rows }),
      );
    }
  });
  resizeObserver.observe(terminalRef.value);
}

function closeSession() {
  if (ws?.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify({ type: "close" }));
  }
  endSession();
}

function connect() {
  ws = new WebSocket(getWsUrl());
  ws.binaryType = "arraybuffer";

  // Timeout: agent never joined
  setTimeout(() => {
    if (status.value === "connecting") {
      endSession();
      ws?.close();
    }
  }, 60000);

  ws.onmessage = async (event) => {
    if (typeof event.data === "string") {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === "joined") {
          // Wait for the connected DOM (which holds terminalRef) to render
          // before initTerminal: it runs synchronously here, but Vue paints the
          // v-else-if branch on the next tick, so terminalRef would still be
          // undefined and initTerminal would bail, leaving `term` null forever
          // -> every later binary frame hits `else if (term)` and is dropped
          // (blank terminal).
          status.value = "connected";
          await nextTick();
          initTerminal();
          return;
        }
        if (msg.type === "close") {
          endSession();
          return;
        }
      } catch {
        // Not JSON, treat as terminal output
      }
      if (term) term.write(event.data);
    } else if (term) {
      term.write(new Uint8Array(event.data));
    }
  };

  // Reconnect on unexpected drop while still connecting
  ws.onclose = () => {
    if (status.value === "connecting") {
      setTimeout(connect, 2000);
      return;
    }
    endSession();
  };

  ws.onerror = () => {
    endSession();
  };
}

onMounted(() => {
  connect();
});

onBeforeUnmount(() => {
  unmounted = true;
  resizeObserver?.disconnect();
  ws?.close();
  term?.dispose();
});
</script>

<style scoped>
.terminal-container {
  background: #1e1e1e;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
}

.terminal-connected {
  position: relative;
  width: 100%;
  height: 100%;
}

.terminal-body {
  width: 100%;
  height: 100%;
}

.terminal-close {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 10;
  background: rgba(255, 255, 255, 0.1);
  color: #d4d4d4;
  border: none;
  border-radius: 4px;
  padding: 2px 8px;
  cursor: pointer;
}

.terminal-close:hover {
  background: rgba(255, 255, 255, 0.25);
}

.terminal-loading,
.terminal-closed {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 12px;
  color: #d4d4d4;
  font-size: 14px;
}
</style>
